/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Tasos Kolokathis on 16/12/25.
 */
import * as _ from "lodash";
import emitter from "@/emitter";
import moment from "moment-timezone";

import AstrumProxyApi from "@/services/AstrumProxy";

import PortalApi from "@/services/Portal";
import WorkerTimer from "@/plugins/WorkerTimer";

import Player from "@/enums/Player";

const livePlayersTimeoutMap = new Map();

export default {
	state: {
		livePlayers: [],

		liveConferenceSnapshot: {},

		dumpBufferPendingStatus: false,

		playerLoudness: null,

		stationIsSilent: false,

		externalMicOn: false
	},

	getters: {
		getLivePlayers: state => () => {
			return state.livePlayers;
		},

		getLivePlayerSnapshot: state => playerKey => {
			if (playerKey) {
				return state.livePlayers.find(p => p.RealtimePlayerKey === playerKey);
			}
			return state.livePlayers[0];
		},

		getLiveConferenceSnapshots: state => playerKey => {
			return playerKey ? state.liveConferenceSnapshot[playerKey] : _.values(state.liveConferenceSnapshot);
		},

		getSIPPeers: state => () => {
			return _.values(state.liveConferenceSnapshot).reduce((array, conf) => {
				return array.concat(
					conf.sipPeers.map(peer => {
						peer.playerKey = peer.player.split(".")[2];
						peer.type = "SIP";
						return peer;
					})
				);
			}, []);
		},

		getWebRTCPeers: state => () => {
			return _.keys(state.liveConferenceSnapshot).reduce((webRTCPeers, playerKey) => {
				return webRTCPeers.concat(
					state.liveConferenceSnapshot[playerKey].webRTCPeers.map(peer => {
						const peerParts = peer.split("_"); // {station_id}_{studio}_{client_id}_{role}-{astrum-random}
						return {
							peerID: peer,
							user: peerParts[0],
							clientID: peerParts[2],
							playerKey: playerKey,
							studio: peerParts[1],
							role: parseInt(peerParts[3].split("-")[0]), //Role includes a unique id that set form Astrum.
							type: "webRTC",
							status: "CONNECTED" //If webRTC peer exists, then it means that it is also connected
						};
					})
				);
			}, []);
		},

		getLiveConferenceSnapshotPlayers: state => () => {
			return _.keys(state.liveConferenceSnapshot);
		},

		dumpBufferPendingStatus: state => () => {
			return state.dumpBufferPendingStatus;
		},

		getPlayerLoudness: state => () => {
			return state.playerLoudness;
		},

		getExternalMicOn: state => () => {
			return state.externalMicOn;
		},

		getStationIsSilent: state => () => {
			return state.stationIsSilent;
		}
	},

	mutations: {
		setLivePlayer(state, livePlayerData) {
			const index = state.livePlayers.findIndex(p => p.RealtimePlayerKey === livePlayerData.RealtimePlayerKey);
			if (index > -1) {
				state.livePlayers.splice(index, 1, livePlayerData);
			} else {
				state.livePlayers.push(livePlayerData);
			}
		},

		setLivePlayerConferenceSnapshot(state, {playerKey, conferenceSnapshot}) {
			if (
				!state.liveConferenceSnapshot[playerKey]?.timestamp ||
				state.liveConferenceSnapshot[playerKey]?.timestamp < conferenceSnapshot.timestamp
			) {
				state.liveConferenceSnapshot[playerKey] = conferenceSnapshot;
			}
		},

		removeLivePlayer(state, playerKey) {
			const index = state.livePlayers.findIndex(p => p.RealtimePlayerKey === playerKey);
			if (index > -1) {
				state.livePlayers.splice(index, 1);
			}
			delete state.liveConferenceSnapshot[playerKey];
		},

		setDumpBufferPendingStatus(state, status) {
			state.dumpBufferPendingStatus = status;
		},

		setPlayerLoudness(state, loudness) {
			state.playerLoudness = loudness;
		},

		setExternalMicOn(state, externalMicOn) {
			state.externalMicOn = externalMicOn;
		},

		resetPlayerState(state) {
			state.livePlayers = [];
			state.liveConferenceSnapshot = {};
			state.playerLoudness = null;
			state.externalMicOn = false;
		},

		setStationIsSilent(state, isSilent) {
			state.stationIsSilent = isSilent;
		}
	},

	actions: {
		async getAstrumPlayers({getters, commit}) {
			const accountId = getters.getOrgID();
			const stationId = getters.liveModeSelectedStationID();

			return AstrumProxyApi.getAstrumPlayers({accountId, stationId})
				.then(results => {
					results.data.forEach(p => {
						commit("setLivePlayer", {RealtimePlayerKey: p.key.split(".")[2]});
					});
				})
				.catch(e => {
					console.error(e);
				});
		},

		getAstrumPlayerLiveState({getters, commit, dispatch}, studioSettings) {
			return AstrumProxyApi.getLiveState().then(async response => {
				if (response.status === 200) {
					const currentStudioLiveState = response.data.find(({sipAddress}) =>
						studioSettings.sip_connection_address.includes(sipAddress)
					);
					if (currentStudioLiveState) {
						const connectedStationID = currentStudioLiveState.playerKey.split(".")[1];

						if (connectedStationID !== getters.liveModeSelectedStationID()) {
							// If connected station is different from the selected station in live mode
							PortalApi.getStation(connectedStationID).then(response => {
								const st = {
									...response.data,
									StationID: response.data.id,
									Logo: `${response.data.logo}?${moment().valueOf()}`
								};
								dispatch("getStation", st)
									.then(station => {
										if (station) {
											commit("appendStations", [station]);
											emitter.emit("set-selected-station", station);
										}
									})
									.catch(err => {
										console.error(err);
									});
							});
						}
					}
				}
			});
		},

		createTempLivePlayer({getters}) {
			const station = getters.liveModeSelectedStation();
			const payload = {
				namespace: null,
				key: `${station.OrganizationID}.${station.StationID}.astrum-temp-${moment().valueOf()}`,
				mediaType: "aac",
				bitrate: 48000,
				sampleRate: 48000,
				outputUrl: "",
				password: "",
				group: `${station.pretty_url}`,
				purpose: 2,
				sound4: null,
				nielsenParams: null,
				outputConfigs: [
					{
						url: "",
						password: ""
					}
				]
			};
			return AstrumProxyApi.createTempLivePlayer(station.OrganizationID, station.StationID, payload);
		},

		livePlayerLiveMetadataResponse({commit}, message) {
			const regMetadata = /SCRIPT\^MIC=/;

			if (regMetadata.test(message.metadata)) {
				commit("setExternalMicOn", ~~message.metadata.charAt(message.metadata.length - 1));
			}
		},

		playerOnline({getters, commit, dispatch}, {message}) {
			if (message.PlayerConfig?.DeploymentType === Player.DeploymentTypes.Astrum.val) {
				//Astrum Player
				let userPeer = getters.getSIPPeers().find(p => p.studio === getters.getStudioSettings()?.reference);
				if (!userPeer) {
					userPeer = getters.getWebRTCPeers().find(p => {
						return p.studio === getters.getStudioSettings()?.reference && p.user === getters.getUserID();
					});
				}

				if (userPeer) {
					let micOn = userPeer.type === "webRTC" ? getters.isMicOn() : getters.getExternalMicOn();
					dispatch("connectStudio", userPeer.role);
					userPeer.type === "webRTC" ? commit("setMicOnOff", micOn) : commit("setExternalMicOn", micOn);
				}
			}
		},

		playerHeartBeat({getters, commit, dispatch}, message) {
			const thereAreLivePlayers = !!getters.getLivePlayers().length;
			const player = message.PlayerConfig;

			if (thereAreLivePlayers) {
				if (player?.DeploymentType === Player.DeploymentTypes.Astrum.val) {
					if (message.RealtimeLiveConsoleSnapshot) {
						dispatch("livePlayerSnapshotResponse", message.RealtimeLiveConsoleSnapshot);
					}

					if (livePlayersTimeoutMap.has(message.RealtimePlayerKey)) {
						WorkerTimer.clearTimeout(livePlayersTimeoutMap.get(message.RealtimePlayerKey));
					}

					const timeoutId = WorkerTimer.setTimeout(() => {
						commit("removeLivePlayer", message.RealtimePlayerKey);
						dispatch("getAstrumPlayers");
					}, 12000);
					livePlayersTimeoutMap.set(message.RealtimePlayerKey, timeoutId);
				}

				if (message.RealtimePlayerLoudness) {
					if (getters.isSignalerConnected()) {
						if (getters.getLivePlayerSnapshot()?.RealtimePlayerKey === message.RealtimePlayerKey) {
							commit("setPlayerLoudness", message.RealtimePlayerLoudness);
							console.debug(
								`${player.PlayerKey} - RealtimePlayerLoudness `,
								message.RealtimePlayerLoudness
							);
						}
					} else {
						commit("setPlayerLoudness", message.RealtimePlayerLoudness);
						console.debug(`${player.PlayerKey} - RealtimePlayerLoudness `, message.RealtimePlayerLoudness);
					}
				}
			} else {
				// If there are no live players (Astrum), we need to get the silence from the primary player's heartbeat
				if (player?.PlayerPurposeType === Player.PurposeTypes.Primary.val) {
					const realtimeDefaultMixer = message.RealtimeMixerSnapshots?.Default_Mixer;
					const playerMixer = player.MixerDictionary?.Default_Mixer;
					const silenceDetection = playerMixer?.AudioLevelsDSP?.AudioLevelSkill;
					const realtimeAudioChannelSnapshots = realtimeDefaultMixer?.RealtimeAudioChannelSnapshots;

					if (silenceDetection && realtimeAudioChannelSnapshots) {
						let leftAlertMode,
							rightAlertMode,
							alertMode = null;
						if (realtimeAudioChannelSnapshots.Left?.LastLowLevelAudioLevelAlert) {
							leftAlertMode = realtimeAudioChannelSnapshots.Left.LastLowLevelAudioLevelAlert?.AlertMode;
						}
						if (realtimeAudioChannelSnapshots.Right?.LastLowLevelAudioLevelAlert) {
							rightAlertMode = realtimeAudioChannelSnapshots.Right.LastLowLevelAudioLevelAlert?.AlertMode;
						}

						if (
							(rightAlertMode === Player.AlertModes.On.val &&
								leftAlertMode === Player.AlertModes.On.val) ||
							(_.isUndefined(rightAlertMode) && leftAlertMode === Player.AlertModes.On.val) ||
							(_.isUndefined(leftAlertMode) && rightAlertMode === Player.AlertModes.On.val)
						) {
							alertMode = Player.AlertModes.On.val;
						} else {
							alertMode = Player.AlertModes.Off.val;
						}
						commit("setStationIsSilent", alertMode === Player.AlertModes.On.val);
						console.debug(`${player.PlayerKey} is silent `, alertMode === Player.AlertModes.On.val);
					}
				}
			}
		}
	}
};